import type { Id } from 'convex/_generated/dataModel';

const MIME_TYPES_BY_EXTENSION: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
};

const SUPPORTED_ATTACHMENT_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
]);

const getUploadContentType = (file: File): string | undefined => {
  if (SUPPORTED_ATTACHMENT_MIME_TYPES.has(file.type)) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase();
  return (extension ? MIME_TYPES_BY_EXTENSION[extension] : undefined) ?? (file.type || undefined);
};

export const uploadFile = async (uploadUrl: string, file: File): Promise<Id<'_storage'>> => {
  const contentType = getUploadContentType(file);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5 * 60 * 1000);
  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      signal: controller.signal,
      body: file,
      ...(contentType ? { headers: { 'Content-Type': contentType } } : {}),
    });

    if (!response.ok) {
      throw new Error(`Storage upload failed with HTTP ${response.status}`);
    }

    const payload: unknown = await response.json().catch(() => null);
    if (!payload || typeof payload !== 'object') throw new Error('Storage upload returned no storage ID');

    const storageId = 'storageId' in payload ? payload.storageId : 'id' in payload ? payload.id : null;
    if (typeof storageId !== 'string' || storageId.length === 0) throw new Error('Storage upload returned no storage ID');
    return storageId as Id<'_storage'>;
  } catch (error) {
    if (controller.signal.aborted) throw new Error('Storage upload timed out. Please try again.');
    throw error;
  } finally {
    clearTimeout(timeout);
  }
};

type ChatAttachmentArgs = {
  dutyId: Id<'duties'>;
  file: File;
  createUploadUrl: (args: { dutyId: Id<'duties'> }) => Promise<string>;
  finalizeAttachment: (args: {
    dutyId: Id<'duties'>;
    fileId: Id<'_storage'>;
    name: string;
    contentType: string;
    size: number;
  }) => Promise<{ messageId: Id<'messages'>; documentId: Id<'serviceDocuments'> }>;
  markRead: (args: { dutyId: Id<'duties'> }) => Promise<null>;
  onMarkReadError: (error: Error) => void;
};

export const uploadChatAttachment = async ({ dutyId, file, createUploadUrl, finalizeAttachment, markRead, onMarkReadError }: ChatAttachmentArgs): Promise<void> => {
  const contentType = getUploadContentType(file) ?? '';
  const uploadUrl = await createUploadUrl({ dutyId });
  const fileId = await uploadFile(uploadUrl, file);
  await finalizeAttachment({ dutyId, fileId, name: file.name, contentType, size: file.size });
  await markRead({ dutyId }).catch((error) => onMarkReadError(error instanceof Error ? error : new Error(String(error))));
};
